import { createContext, useContext, useEffect, useState } from 'react';
import Keycloak, { KeycloakTokenParsed } from 'keycloak-js';
import Loading from 'src/pages/Status/Loading';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { AUTH_BEARER_TOKEN_KEY, FEATURE_FLAGS } from 'src/constants';
import { IUser, UserToken } from '@publishing-engine/domain/models';
import { centralAuthUrl, clientAuthRealm } from 'src/utils/env';
import { IKeycloakUser } from '@publishing-engine/domain/types';
import { syncUser, recordLogin } from 'src/api/queries';
import { useFlag } from '@unleash/proxy-client-react';
import { RootAuth } from 'src/pages/Auth/RootAuth';
import * as Sentry from '@sentry/react';
import { getBaseAppUrl } from 'src/utils/baseAppUrl';
import { replaceEncodedReturnToDomain } from './helpers';
import { usePageFocus } from 'src/hooks/usePageFocus';
import { useUserDeviceData } from '../../hooks/useUserDeviceData';
import { IAuthRealm } from '@publishing-engine/domain/utils';

export const CENTRAL_AUTH_CLIENT_ID = 'publishing-engine-keycloak';

export interface KeycloakContextProps {
  keycloak: Keycloak | null;
  logout: (redirectUri?: string) => void;
  switchUser: (email: string) => void;
  currentUser: IUser;
  authRealm: IAuthRealm | undefined;
}

const KeycloakContext = createContext<KeycloakContextProps | undefined>(
  undefined
);

const isExpiredDate = (exp: number) => {
  const now = Math.floor(Date.now() / 1000);
  return now >= exp;
};

// Custom hook to use the Keycloak context
export const useKeycloak = (): KeycloakContextProps => {
  const context = useContext(KeycloakContext);
  if (!context) {
    throw new Error('useKeycloak must be used within a KeycloakProvider');
  }
  return context;
};

const AuthProvider = ({ children }: { children: JSX.Element }) => {
  const isUserCreationAndAuthTokenActive = useFlag(
    FEATURE_FLAGS.jedis389UserCreationAndAuthToken
  );
  const [, setStoredToken] = useLocalStorage<UserToken | null>(
    AUTH_BEARER_TOKEN_KEY,
    null
  );

  const { setUserDeviceData, resetUserDeviceData } = useUserDeviceData();

  const [loading, setLoading] = useState(true);
  const [authRealm, setAuthRealm] = useState<IAuthRealm | undefined>(undefined);
  const [keycloak, setKeycloak] = useState<Keycloak | null>(null);
  const [user, setUser] = useState<IUser>({
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    affiliationDetails: {
      organizationName: '',
      ringgoldId: '',
      city: ''
    },
    status: 'invited'
  });

  const keycloakInstance = new Keycloak({
    clientId: CENTRAL_AUTH_CLIENT_ID,
    url: centralAuthUrl,
    realm: import.meta.env.VITE_CENTRAL_AUTH_CLIENT_REALM
  });

  const handleSyncUser = async (idTokenParsed: KeycloakTokenParsed) => {
    if (!idTokenParsed.sub) return;
    const {
      sub,
      email,
      family_name,
      given_name,
      affiliation,
      orcid,
      ringgold_id,
      idp,
      affiliation_country,
      affiliation_location,
      salutation,
      auth_time
    } = idTokenParsed;

    await syncUser({
      sub,
      email,
      family_name,
      given_name,
      affiliation,
      orcid,
      ringgold_id,
      idp,
      affiliation_country,
      affiliation_location,
      salutation,
      auth_time
    });

    return;
  };

const handleSessionExpired = () => {
  setStoredToken(null);
  resetUserDeviceData();

  keycloak?.login({
    redirectUri: getBaseAppUrl(),
    prompt: 'login'
  });
};

  const handleLogout = (redirectUriOverride?: string) => {
    setStoredToken(null);
    resetUserDeviceData();
    const redirectUri = redirectUriOverride ?? getBaseAppUrl();
    const redirectProperty: Record<string, string> = {};

    if (redirectUri) {
      redirectProperty.redirectUri = redirectUri;
    }

    // idp_logout_url overrides the logout url
    if (
      keycloak?.tokenParsed?.idp_logout_url &&
      !keycloak?.tokenParsed?.is_fixture //this var (keycloak?.tokenParsed?.is_fixture) can be removed when all demos use this version and idp_logout_url-> undefined for fixture users
    ) {
      redirectProperty.redirectUri = keycloak?.tokenParsed?.idp_logout_url;
    }

    if (window.location.hostname !== 'localhost') {
      const idpLogoutUrl = keycloak?.tokenParsed?.idp_logout_url;
      redirectProperty.redirectUri = idpLogoutUrl
        ? replaceEncodedReturnToDomain(idpLogoutUrl, redirectUri)
        : redirectUri;
    } else {
      redirectProperty.redirectUri = redirectUriOverride ?? window.location.origin;
    }

    keycloak?.logout(redirectProperty);
  };

  const switchUser = async (email: string) => {
    if (!keycloak) return;

    try {
      const idToken = keycloak.idToken;
      const endSessionUrl = `${keycloak.authServerUrl}/realms/${keycloak.realm}/protocol/openid-connect/logout`;

      await fetch(endSessionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          client_id: keycloak.clientId!,
          refresh_token: keycloak.refreshToken ?? '',
          id_token_hint: idToken ?? ''
        })
      });

      await keycloak.login({
        loginHint: email,
        prompt: 'login'
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to switch user:', error);
    }
  };

  const setUserFromToken = () => {
    const {
      given_name,
      family_name,
      sub,
      email,
      affiliation,
      orcid,
      ringgold_id,
      idp,
      status,
      salutation
    } = keycloak?.idTokenParsed as unknown as IKeycloakUser;
    setUser({
      firstName: given_name,
      lastName: family_name,
      id: sub,
      email,
      affiliationDetails: {
        organizationName: affiliation ?? 'Unknown',
        ringgoldId: ringgold_id ?? ''
      },
      orcid,
      idp,
      status,
      salutation
    });

    // When we log in, we set the user in Sentry
    // So we know from which user the error is coming from
    Sentry.setUser({
      id: sub,
      email,
      username: `${given_name} ${family_name}`
    });
  };

  usePageFocus(() => {
    const expirationDate = keycloak?.tokenParsed?.exp;
    if (!expirationDate || !isExpiredDate(expirationDate)) return;

    // Token is expired on tab focus. Try the refresh token first (valid for
    // refresh_expires_in = 7200 s) before forcing a full logout.
    keycloak!
      .updateToken(0)
      .then(refreshed => {
        if (refreshed) {
          const tokenToStore = isUserCreationAndAuthTokenActive
            ? keycloak!.token
            : keycloak!.idToken;
          if (tokenToStore) setStoredToken(tokenToStore);
        } else {
          // updateToken(0) returned false — token is still considered valid by
          // Keycloak despite our local clock saying it's expired (clock skew).
          // Nothing to do; the scheduled onTokenExpired will handle it.
        }
      })
      .catch(() => {
        // Refresh token is also expired or the server rejected it — log out.
        handleSessionExpired();
      });
  });

  useEffect(() => {
    setLoading(true);
    const load = async () => {
      try {
        const silentCheckSsoRedirectUri = window.location.origin + '/silent-check-sso.html';

        await keycloakInstance.init({
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri,
          silentCheckSsoFallback: false,
          checkLoginIframe: false,
          pkceMethod: 'S256'
        });
        setKeycloak(keycloakInstance);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (
      !keycloak ||
      !keycloak.idToken ||
      !keycloak.token ||
      !keycloak.idTokenParsed ||
      !keycloak?.authenticated
    )
      return;

    if (isUserCreationAndAuthTokenActive) {
      setStoredToken(keycloak.token);
      handleSyncUser(keycloak.idTokenParsed);
      setUserFromToken();
    } else {
      setStoredToken(keycloak.idToken);
      setUserFromToken();
      recordLogin(keycloak.idTokenParsed?.auth_time);
    }
    setAuthRealm(clientAuthRealm);
    setUserDeviceData();
  }, [keycloak]);

  useEffect(() => {
    if (!keycloak?.authenticated) return;

    const refreshToken = async () => {
      try {
        // updateToken(minValidity):
        //   -1 → always refresh (used here so onTokenExpired always issues a new token)
        //   60 → only refresh if expiring within 60 s (was the old value — caused the
        //        proactive call on mount to be a no-op and then the timer to never fire
        //        a second refresh before the 30-min expiry hit)
        const refreshed = await keycloak.updateToken(-1);
        if (refreshed) {
          const tokenToStore = isUserCreationAndAuthTokenActive
            ? keycloak.token
            : keycloak.idToken;
          if (tokenToStore) setStoredToken(tokenToStore);
        }
      } catch (error) {
        handleSessionExpired();
      }
    };

    keycloak.onTokenExpired = refreshToken;

    // Proactive early refresh: schedule a refresh 60 s before the token expires
    // so it is ready well before the 30-min mark, regardless of tab visibility.
    const tokenExp = keycloak.tokenParsed?.exp;
    const now = Math.floor(Date.now() / 1000);
    const msUntilRefresh = tokenExp ? Math.max((tokenExp - now - 60) * 1000, 0) : 0;
    const proactiveRefreshHandle = setTimeout(refreshToken, msUntilRefresh);

    return () => {
      keycloak.onTokenExpired = undefined;
      clearTimeout(proactiveRefreshHandle);
    };
  }, [keycloak, isUserCreationAndAuthTokenActive]);

  if (loading) return <Loading />;

  const providerProps = {
    keycloak,
    logout: handleLogout,
    currentUser: user,
    switchUser,
    authRealm
  };

  return (
    <KeycloakContext.Provider value={providerProps}>
      {!keycloak?.authenticated ? <RootAuth /> : children}
    </KeycloakContext.Provider>
  );
};

export default AuthProvider;
